import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ButtonBase from "@mui/material/ButtonBase";
import CategoryIcon from "@mui/icons-material/Category";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { CompositionService } from "../../librettoEditor/services/CompositionService";
import { CompositionStateStore } from "../../librettoEditor/store/CompositionStateStore";
import { Timestamp } from "../../engine/models";
import { ApplicationStore } from "../../librettoEditor/store/ApplicationStore";
import { TextClip } from "../../engine/clips/text";
import { PlaceholderClip } from "../../engine/clips/placeholder/placeholder";

const PaperPanel: React.FC<{
    title: string;
    children: React.ReactNode;
}> = ({title, children}) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            background: "var(--app-bg-surface)",
            border: "0.5px solid var(--app-border-subtle)",
            borderRadius: "12px",
            px: 1,
            py: 1,
        }}
    >
        <Box sx={{display: "flex", alignItems: "center", px: 0.5, py: 0.5}}>
            <Typography sx={{fontSize: 14, fontWeight: 600, userSelect: "none"}}>{title}</Typography>
        </Box>
        <Divider sx={{my: 0.5}} />
        {children}
    </Box>
);

const optionButtonSx = {
    width: "100%",
    height: 60,
    borderRadius: "8px",
    background: "var(--app-bg-subtle)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.15s ease",
    "&:hover": {
        backgroundColor: "var(--app-border-subtle)",
    },
};

const OptionButton: React.FC<{
    label?: string;
    icon: React.ReactNode;
    onClick: () => void;
    ariaLabel?: string;
}> = ({label, icon, onClick, ariaLabel}) => (
    <ButtonBase disableRipple onClick={onClick} aria-label={ariaLabel ?? label} sx={optionButtonSx}>
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: label ? 0.5 : 0}}>
            {icon}
            {label ? <Typography sx={{fontSize: 11, color: "var(--app-text-primary)"}}>{label}</Typography> : null}
        </Box>
    </ButtonBase>
);


const titleTextStyle = {
    color: "var(--app-text-primary)",
    paddingLeft: "8px",
    paddingRight: "8px",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    fontFamily: "Inter, sans-serif",
    userSelect: "none",
};

export const V2ElementsSidebar = () => {
    const creatingTextClipRef = React.useRef(false);

    const createTextClip = async (preset: "text" | "subtitle" | "title") => {
        if (!CompositionService.isInitialized()) return;
        if (creatingTextClipRef.current) return;
        creatingTextClipRef.current = true;

        try {
            const comp = CompositionService.composition as any;
            const compW = Number(comp?.settings?.width ?? comp?.width ?? 1920) || 1920;
            const compH = Number(comp?.settings?.height ?? comp?.height ?? 1080) || 1080;

            const scriptClip: any = CompositionService.getScriptLayerClip?.();
            const scenes: any[] = scriptClip && typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
            const t = Number(CompositionStateStore.currentTime ?? 0);
            const scene =
                scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) && t < Number(s?.timelineEndSec ?? 0)) ??
                scenes.find((s: any) => t < Number(s?.timelineEndSec ?? 0)) ??
                (scenes.length ? scenes[scenes.length - 1] : null);

            const sceneStart = Math.max(0, Number(scene?.timelineStartSec ?? (Number.isFinite(t) ? t : 0)));
            const sceneEndRaw = Number(scene?.timelineEndSec ?? sceneStart + 5);
            const minDur = 0.25;
            const sceneEnd = Math.max(sceneStart + minDur, sceneEndRaw);

            const tSec = Math.max(0, Number.isFinite(t) ? t : 0);
            const startSec = scene ? Math.min(Math.max(tSec, sceneStart), sceneEnd - minDur) : tSec;
            const stopSec = scene ? sceneEnd : startSec + 5;

            // Ensure an empty script spine exists for empty compositions (script defines bounds).
            try {
                if (!CompositionService.getScriptLayerClip?.()) {
                    await CompositionService.ensureEmptyScriptSpine(stopSec);
                }
            } catch {}

            const presetConfig: {
                name: string;
                text: string;
                fontSize: number;
                fontWeight: string;
                y: number;
                maxWidth: string;
            } = (() => {
                if (preset === "title") {
                    return {
                        name: "Title",
                        text: "Title",
                        fontSize: 80,
                        fontWeight: "600",
                        y: compH * 0.12,
                        maxWidth: "70%",
                    };
                }
                if (preset === "subtitle") {
                    return {
                        name: "Subtitle",
                        text: "Subtitle",
                        fontSize: 54,
                        fontWeight: "600",
                        y: compH * 0.78,
                        maxWidth: "80%",
                    };
                }
                return {
                    name: "Text",
                    text: "Text",
                    fontSize: 56,
                    fontWeight: "500",
                    y: compH * 0.2,
                    maxWidth: "70%",
                };
            })();

            const maxWpx =
                typeof presetConfig.maxWidth === "string"
                    ? (Number.parseFloat(presetConfig.maxWidth) / 100) * compW
                    : Number(presetConfig.maxWidth);

            // TextClip positions are centered (like other visual clips). Approximate a centered Y from the preset's top Y.
            const estH = Math.max(1, presetConfig.fontSize * 1.2);
            const x = Math.max(0, compW / 2);
            const y = Math.max(0, presetConfig.y + estH / 2);

            const clip = new TextClip({
                name: presetConfig.name,
                text: presetConfig.text,
                textAlign: "center" as any,
                maxWidth: presetConfig.maxWidth,
                textStyle: {
                    fontFamily: "Inter",
                    fontSize: presetConfig.fontSize,
                    fontWeight: presetConfig.fontWeight,
                    fontStyle: "normal",
                    fillColor: "#FFFFFF" as any,
                    stroke: { color: "#000000" as any, width: 4, join: "round" },
                } as any,
                position: { x, y } as any,
                start: Timestamp.fromSeconds(startSec),
                stop: Timestamp.fromSeconds(stopSec),
            });

            try {
                await comp.add(clip);
            } catch {}

            try {
                CompositionStateStore.setTitleForClip(clip.id, "Text");
            } catch {}

            try {
                ApplicationStore.startEditingTextClip(clip.id);
            } catch {}

            try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        } finally {
            creatingTextClipRef.current = false;
        }
    };

    const handleAddTextClip = () => void createTextClip("text");
    const handleAddSubtitleTextClip = () => void createTextClip("subtitle");
    const handleAddTitleTextClip = () => void createTextClip("title");

    const handleAddMediaPlaceholderClip = () => {
        if (!CompositionService.isInitialized()) return;

        const comp = CompositionService.composition as any;
        const compW = Number(comp?.settings?.width ?? comp?.width ?? 1920) || 1920;
        const compH = Number(comp?.settings?.height ?? comp?.height ?? 1080) || 1080;

        const scriptClip: any = CompositionService.getScriptLayerClip?.();
        const scenes: any[] = scriptClip && typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
        const t = Math.max(0, Number(CompositionStateStore.currentTime ?? 0) || 0);
        const scene =
            scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) && t < Number(s?.timelineEndSec ?? 0)) ??
            scenes.find((s: any) => t < Number(s?.timelineEndSec ?? 0)) ??
            (scenes.length ? scenes[scenes.length - 1] : null);

        const sceneStart = Math.max(0, Number(scene?.timelineStartSec ?? t));
        const sceneEndRaw = Number(scene?.timelineEndSec ?? (sceneStart + 5));
        const minDur = 0.25;
        const sceneEnd = Math.max(sceneStart + minDur, sceneEndRaw);

        const startSec = scene ? Math.min(Math.max(t, sceneStart), sceneEnd - minDur) : t;
        const stopSec = scene ? sceneEnd : startSec + 5;

        const margin = Math.max(12, Math.min(compW, compH) * 0.02);
        const w = Math.max(120, (compW - margin * 2) * 0.5);
        const h = Math.max(120, compH - margin * 2);

        const clip = new PlaceholderClip({
            name: "Media placeholder",
            label: "Add media",
            placeholderKind: "media",
            position: { x: margin + w / 2, y: compH / 2 } as any,
            width: w as any,
            height: h as any,
            start: Timestamp.fromSeconds(startSec),
            stop: Timestamp.fromSeconds(stopSec),
        } as any);

        try {
            void comp.add(clip);
        } catch {}

        try {
            CompositionStateStore.setTitleForClip(clip.id, "Placeholder");
        } catch {}

        try {
            ApplicationStore.setSelectedClipId(clip.id);
        } catch {}

        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
    };

    const handleAddScreenPlaceholderClip = () => {
        if (!CompositionService.isInitialized()) return;

        const comp = CompositionService.composition as any;
        const compW = Number(comp?.settings?.width ?? comp?.width ?? 1920) || 1920;
        const compH = Number(comp?.settings?.height ?? comp?.height ?? 1080) || 1080;

        const scriptClip: any = CompositionService.getScriptLayerClip?.();
        const scenes: any[] = scriptClip && typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
        const t = Math.max(0, Number(CompositionStateStore.currentTime ?? 0) || 0);
        const scene =
            scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) && t < Number(s?.timelineEndSec ?? 0)) ??
            scenes.find((s: any) => t < Number(s?.timelineEndSec ?? 0)) ??
            (scenes.length ? scenes[scenes.length - 1] : null);

        const sceneStart = Math.max(0, Number(scene?.timelineStartSec ?? t));
        const sceneEndRaw = Number(scene?.timelineEndSec ?? (sceneStart + 5));
        const minDur = 0.25;
        const sceneEnd = Math.max(sceneStart + minDur, sceneEndRaw);

        const startSec = scene ? Math.min(Math.max(t, sceneStart), sceneEnd - minDur) : t;
        const stopSec = scene ? sceneEnd : startSec + 5;

        const margin = Math.max(12, Math.min(compW, compH) * 0.02);
        const w = Math.max(120, compW - margin * 2);
        const h = Math.max(120, compH - margin * 2);

        const clip = new PlaceholderClip({
            name: "Screen placeholder",
            label: "Add media",
            placeholderKind: "screen",
            position: { x: compW / 2, y: compH / 2 } as any,
            width: w as any,
            height: h as any,
            start: Timestamp.fromSeconds(startSec),
            stop: Timestamp.fromSeconds(stopSec),
        } as any);

        try {
            void comp.add(clip);
        } catch {}

        try {
            CompositionStateStore.setTitleForClip(clip.id, "Placeholder");
        } catch {}

        try {
            ApplicationStore.setSelectedClipId(clip.id);
        } catch {}

        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
    };

    const handleAddCameraPlaceholderClip = () => {
        if (!CompositionService.isInitialized()) return;

        const comp = CompositionService.composition as any;
        const compW = Number(comp?.settings?.width ?? comp?.width ?? 1920) || 1920;
        const compH = Number(comp?.settings?.height ?? comp?.height ?? 1080) || 1080;

        const scriptClip: any = CompositionService.getScriptLayerClip?.();
        const scenes: any[] = scriptClip && typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
        const t = Math.max(0, Number(CompositionStateStore.currentTime ?? 0) || 0);
        const scene =
            scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) && t < Number(s?.timelineEndSec ?? 0)) ??
            scenes.find((s: any) => t < Number(s?.timelineEndSec ?? 0)) ??
            (scenes.length ? scenes[scenes.length - 1] : null);

        const sceneStart = Math.max(0, Number(scene?.timelineStartSec ?? t));
        const sceneEndRaw = Number(scene?.timelineEndSec ?? (sceneStart + 5));
        const minDur = 0.25;
        const sceneEnd = Math.max(sceneStart + minDur, sceneEndRaw);

        const startSec = scene ? Math.min(Math.max(t, sceneStart), sceneEnd - minDur) : t;
        const stopSec = scene ? sceneEnd : startSec + 5;

        const margin = Math.max(12, Math.min(compW, compH) * 0.02);
        const size = Math.max(120, Math.min(compW - margin * 2, compH - margin * 2) * 0.35);

        const clip = new PlaceholderClip({
            name: "Camera placeholder",
            label: "Add media",
            placeholderKind: "camera",
            position: { x: compW / 2, y: compH / 2 } as any,
            width: size as any,
            height: size as any,
            start: Timestamp.fromSeconds(startSec),
            stop: Timestamp.fromSeconds(stopSec),
        } as any);

        try {
            void comp.add(clip);
        } catch {}

        try {
            CompositionStateStore.setTitleForClip(clip.id, "Placeholder");
        } catch {}

        try {
            ApplicationStore.setSelectedClipId(clip.id);
        } catch {}

        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
    };

    return (
        <Box sx={{height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: "8px", p: "3px"}}>
            <Box sx={{display: "flex", flexDirection: "row", alignItems: "flex-start", marginLeft: "8px"}}>
                <Box sx={{width: "24px", height: "24px", color: "var(--app-text-primary)"}}>
                    <CategoryIcon />
                </Box>
                <Box sx={titleTextStyle}>Elements</Box>
            </Box>

            <PaperPanel title="Text">
                <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", px: 0.5, pb: 1, marginTop: 1}}>
                    <OptionButton
                        ariaLabel="Text"
                        icon={<Typography sx={{fontSize: 12, fontWeight: 500, color: "var(--app-text-primary)"}}>Text</Typography>}
                        onClick={handleAddTextClip}
                    />
                    <OptionButton
                        ariaLabel="Subtitle"
                        icon={<Typography sx={{fontSize: 14, fontWeight: 500, color: "var(--app-text-primary)"}}>Subtitle</Typography>}
                        onClick={handleAddSubtitleTextClip}
                    />
                    <OptionButton
                        ariaLabel="Title"
                        icon={<Typography sx={{fontSize: 16, fontWeight: 600, color: "var(--app-text-primary)"}}>Title</Typography>}
                        onClick={handleAddTitleTextClip}
                    />
                </Box>
            </PaperPanel>

            <PaperPanel title="Placeholder">
                <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", px: 0.5, pb: 1, marginTop: 1,}}>
                    <OptionButton label="Media" icon={<PermMediaOutlinedIcon sx={{fontSize: 20}} />} onClick={handleAddMediaPlaceholderClip} />
                    <OptionButton label="Screen" icon={<DesktopWindowsOutlinedIcon sx={{fontSize: 20}} />} onClick={handleAddScreenPlaceholderClip} />
                    <OptionButton label="Camera" icon={<PersonOutlinedIcon sx={{fontSize: 20}} />} onClick={handleAddCameraPlaceholderClip} />
                </Box>
            </PaperPanel>
        </Box>
    );
};

export default V2ElementsSidebar;
